import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMask } from "@react-input/mask";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

type FormDataType = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

export default function ContactPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const formRef = useReveal<HTMLDivElement>();

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormDataType>();

  const { ref: hookFormRef, ...phoneRegister } = register("phone", {
    required: "Phone is required",
    minLength: { value: 19, message: "Enter full phone number" },
  });

  const maskRef = useMask({
    mask: "+44 (___) ___-__-__",
    replacement: { _: /\d/ },
  });

  const setRefs = (el: HTMLInputElement | null) => {
    maskRef.current = el;
    hookFormRef(el);
  };

  const onSubmit = async (data: FormDataType) => {
    setLoading(true);
    setError(null);

    let text = "";
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: data.subject,
      to_email: "support@vantorylabs.com",
      subject: " ",
    };

    try {
      const res = await fetch("https://rocknlabs.com/api/support/request_form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Server error");

      setSubmitted(true);
      reset();
    } catch {
      setError("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // shared input classes
  const inputCls = (hasError: boolean) =>
    `w-full rounded-xl border bg-sand/40 px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:outline-none transition-colors ${
      hasError ? "border-terracotta/60 focus:border-terracotta" : "border-ink/10 focus:border-ink/30"
    }`;

  return (
    <SiteLayout navbarVariant="overlay">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              Get in touch
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Let&apos;s build
              <br />
              <span className="italic font-light">something great.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

            {/* Form */}
            <div ref={formRef} className="lg:col-span-7" data-reveal>
              <h2 className="text-display text-3xl md:text-4xl mb-3">
                Send us a message
              </h2>
              <p className="text-ink/60 text-base leading-relaxed mb-10 max-w-lg">
                Fill out the form below and our team will get back to you within 24 hours.
              </p>

              {submitted ? (
                <div className="rounded-3xl bg-lime-accent/20 border border-lime-accent/30 p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-lime-accent mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-ink" />
                  </div>
                  <h3 className="text-display text-2xl mb-2">Message sent!</h3>
                  <p className="text-ink/60 text-sm">
                    Thank you for reaching out. We will reply as soon as possible.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink px-5 py-3 text-xs font-semibold uppercase tracking-widest2 whitespace-nowrap cursor-pointer hover:bg-ink hover:text-cream transition-all duration-200"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>

                  {/* Name + Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-widest2 text-ink/50 mb-2">
                        Full Name
                      </label>
                      <input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        className={inputCls(!!errors.name)}
                        {...register("name", {
                          required: "Name is required",
                          minLength: { value: 2, message: "At least 2 characters" },
                        })}
                      />
                      {errors.name && (
                        <p className="mt-1.5 text-xs text-terracotta">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-widest2 text-ink/50 mb-2">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        className={inputCls(!!errors.email)}
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Enter a valid email",
                          },
                        })}
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-xs text-terracotta">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone + Subject */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-xs uppercase tracking-widest2 text-ink/50 mb-2">
                        Phone
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        placeholder="+44 (___) ___-__-__"
                        className={inputCls(!!errors.phone)}
                        ref={setRefs}
                        {...phoneRegister}
                      />
                      {errors.phone && (
                        <p className="mt-1.5 text-xs text-terracotta">{errors.phone.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-xs uppercase tracking-widest2 text-ink/50 mb-2">
                        Subject
                      </label>
                      <select
                        id="subject"
                        className={`${inputCls(!!errors.subject)} appearance-none cursor-pointer`}
                        {...register("subject", { required: "Please select a topic" })}
                      >
                        <option value="">Select a topic</option>
                        <option value="general">General inquiry</option>
                        <option value="project">Project consultation</option>
                        <option value="partnership">Partnership</option>
                        <option value="careers">Careers</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.subject && (
                        <p className="mt-1.5 text-xs text-terracotta">{errors.subject.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-widest2 text-ink/50 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      placeholder="Tell us about your project or question..."
                      className={`${inputCls(!!errors.message)} resize-none`}
                      {...register("message", {
                        required: "Message is required",
                        minLength: { value: 10, message: "At least 10 characters" },
                        maxLength: { value: 500, message: "Max 500 characters" },
                      })}
                    />
                    <div className="mt-1 text-right text-[10px] text-ink/30 uppercase tracking-widest2">
                      Max 500 characters
                    </div>
                    {errors.message && (
                      <p className="mt-1 text-xs text-terracotta">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Global error */}
                  {error && (
                    <div className="rounded-xl bg-terracotta/10 border border-terracotta/20 px-4 py-3 text-sm text-terracotta">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-7 py-4 text-xs font-semibold uppercase tracking-widest2 whitespace-nowrap cursor-pointer hover:bg-ink/80 transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send message
                        <i className="ri-arrow-right-line text-base" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact info */}
            <div className="lg:col-span-5 lg:pl-8" data-reveal data-reveal-delay="80">
              <div className="rounded-3xl bg-ink p-8 md:p-10 text-cream">
                <h3 className="text-display text-2xl mb-6">Contact details</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cream/10 shrink-0">
                      <i className="ri-map-pin-line text-lg text-lime-accent" />
                    </div>
                    <div>
                      <div className="text-cream font-medium text-sm">
                        109-111 Fulham Palace Road
                      </div>
                      <div className="text-cream/60 text-sm">
                        London, W6 8JA, United Kingdom
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cream/10 shrink-0">
                      <i className="ri-phone-line text-lg text-lime-accent" />
                    </div>
                    <div>
                      <div className="text-cream font-medium text-sm">Phone</div>
                      <a
                        href="tel:+447451257361"
                        className="text-cream/60 text-sm hover:text-lime-accent transition-colors"
                      >
                        +44 7451 257 361
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cream/10 shrink-0">
                      <i className="ri-mail-line text-lg text-lime-accent" />
                    </div>
                    <div>
                      <div className="text-cream font-medium text-sm">Email</div>
                      <a
                        href="mailto:support@vantorylabs.com"
                        className="text-cream/60 text-sm hover:text-lime-accent transition-colors"
                      >
                        support@vantorylabs.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-cream/10">
                  <div className="text-xs uppercase tracking-widest2 text-cream/40 mb-3">
                    Business hours
                  </div>
                  <div className="text-cream/70 text-sm">
                    Mon – Fri · 9:00am – 6:00pm GMT
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
